import React, {useEffect, useState, useMemo} from 'react';
import s from '../../../PersonalOffice/MyOrders/MyOrdersForManager.module.css';
import {Form, Formik} from 'formik';
import {RequestOrdersDataType} from '../../../types/TypesUserOrders';
import MonthRangeFilter from './MonthRangeFilter';
import FilterMonth from '../Components/FilterMonth';
import {useDebounce} from 'use-debounce';
import TextFilterField from '../Components/TextFilterField';
import {useTranslation} from 'react-i18next';
import FilterToggleButton from '../../Buttons/FilterToggleButton';
import FilterSearchButton from '../../Buttons/FilterSearchButton';
import LightGreenButton from '../../Buttons/LightGreenButton';
import GreenButton from '../../Buttons/GreenButton';
import * as Yup from 'yup';

import {
	scrollToTop,
	submitFilterFieldCleaner,
} from '../../../assets/utils/utils';
import {monthNow} from '../../../assets/utils/dateUtils';
import {useMonthRangeFilter} from '../../../hooks/useMonthRangeFilter';

// Схема валидации без ограничения product_id только цифрами
const getValidationFilterFormListStore = (t: (key: string) => string) => Yup.object().shape({
	order_id: Yup.string().test(
		'is-number',
		t('Мають бути цифри'),
		(value) => {
			if (!value) return true;
			return !isNaN(Number(value));
		},
	),
	product_id: Yup.string(), // Разрешаем любой текст
});

type Props = {
	setFilters: (filters: RequestOrdersDataType) => void;
};

const MyOrdersListStoreFilter: React.FC<Props> = ({setFilters}) => {
	const {t} = useTranslation();
	const [filterMenu, setFilterMenu] = useState(false);

	const monthRangeFilter = useMonthRangeFilter();
	const [search, setSearch] = useState<string>('');
	const [debounceSearch] = useDebounce(search, 500);
	const statusesDelivery = [
		{label: t('Чернетка'), value: 'created'},
		{label: t('Нове'), value: 'confirmed'},
		{label: t('Відхилено'), value: 'rejected'},
		{label: t('Підтверджено'), value: 'approved'},
		{label: t('Збирається'), value: 'packed'},
		{label: t('Запаковано'), value: 'packaged'},
		{label: t('Відправлено'), value: 'shipped'},
		{label: t('Отримано'), value: 'received'},
	];

	const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		// Разрешаем ввод любого текста для поиска по названию продукта
		setSearch(e.currentTarget.value);
	};
	const initialValues: RequestOrdersDataType & {month?: string} = useMemo(() => ({
		filter_active: 1,
		limit: '', //кількість замовлень
		from_date: '', // дата початку
		to_date: '', // дата кінцева
		searchtext: '', // введене у пошук
		product_id: '', // назва товару
		order_id: '',
		status: '', // статус
		offset: '',
		month: monthRangeFilter.month,
	}), [monthRangeFilter.month]);
	useEffect(() => {
		const trimmedSearch = debounceSearch.trim();
		if (trimmedSearch.length === 0) {
			// При пустом поиске очищаем фильтр
			setFilters({searchtext: '', offset: 0, filter_active: 0});
		} else if (trimmedSearch.length >= 3) {
			// Отправляем запрос только если больше 3 символов (поиск по названию продукта)
			setFilters({searchtext: trimmedSearch, filter_active: 0, offset: 0});
		}
	}, [debounceSearch, setFilters]);

	const handleSubmit = (values: RequestOrdersDataType & {month?: string}) => {
		// Используем даты из хука
		const filteredValues = submitFilterFieldCleaner({
			...values,
			from_date: monthRangeFilter.fromDate,
			to_date: monthRangeFilter.toDate,
		});
		setFilters({...filteredValues, filter_active: 1, offset: 0});
		setFilterMenu(false);
		scrollToTop('smooth');
	};
	return (
		<Formik
			validationSchema={getValidationFilterFormListStore(t)}
			initialValues={initialValues}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			{({
				values,
				handleSubmit,
				setFieldValue,
				resetForm,
				handleChange,
				touched,
				errors,
			}) => {
				// Синхронизируем месяц из формы с хуком
				React.useEffect(() => {
					if (values.month && values.month !== monthRangeFilter.month) {
						monthRangeFilter.setMonth(values.month);
					}
				}, [values.month]);
				
				// Преобразуем дату месяца в название месяца для отображения
				const getMonthName = (monthString: string): string => {
					if (!monthString) return '';
					const date = new Date(monthString);
					const fullMonthName = date.toLocaleString('uk-UA', {month: 'long'});
					const year = date.getFullYear();
					return `${fullMonthName} ${year}`;
				};
				
				// Обработка изменения месяца - обновляем количество дней и сбрасываем ползунки
				const handleMonthChange = (month: string) => {
					monthRangeFilter.setMonth(month);
					setFieldValue('month', month);
				};
				
				return (
					<>
						<div className="filter-team__header">
							<FilterToggleButton
								isActive={filterMenu}
								onClick={() => setFilterMenu(!filterMenu)}
								variant="blue"
								activeText={t('закрити')}
								inactiveText={t('додати фільтр')}
								className={s.btn}
							/>
							<FilterSearchButton
								value={search}
								onChange={handleChangeSearch}
								placeholder={t('Пошук')}
							/>
						</div>
						<div
							className={`filter-team__filter-item item-filter ${!filterMenu ? '' : 'active'}`}
						>
							<div className="item-filter__wrapper">
								<Form
									onSubmit={handleSubmit}
									className="item-filter__form form-filter"
								>
									<FilterMonth
										currentMonthName={getMonthName(monthRangeFilter.month)}
										setCurrentMonthName={(name) => {
											// FilterMonth устанавливает название месяца, но нам нужна дата
											// Поэтому используем handleMonthChange когда устанавливается месяц через Formik
										}}
										setAmountOfMonth={(amount) => {
											// Количество дней управляется хуком
										}}
										setCurrentMonth={handleMonthChange}
									/>
									<div className="form-filter__item form-filter__item-range">
										<div className="form-filter__label">{t('Дата')}</div>
										<div className="form-filter__row">
											<MonthRangeFilter
												sliderValue={monthRangeFilter.adaptedRangeValue}
												displayValue={monthRangeFilter.displayRangeValue}
												onChange={monthRangeFilter.handleRangeChange}
												maxValue={monthRangeFilter.daysInMonth - 1}
												maxDisplayValue={monthRangeFilter.daysInMonth}
											/>
										</div>
									</div>
									<TextFilterField
										label={`ID ${t('замовлення')}`}
										fieldName={'order_id'}
										placeholder={t('123456789')}
										value={values.order_id ?? ''}
										touched={touched.order_id !== undefined}
										error={errors.order_id !== undefined}
									/>
									<TextFilterField
										label={t('Назва товару')}
										fieldName={'product_id'}
										placeholder={t('Назва')}
										value={values.product_id ?? ''}
										touched={touched.product_id !== undefined}
										error={errors.product_id !== undefined}
									/>
									<div className="form-filter__item form-filter__item-checkbox1">
										<div className="form-filter__item form-filter__item-select2">
											<div className="item-select2__label">
												{t('Статус')}
											</div>
											<select
												onChange={handleChange}
												className="form-filter__select2"
												name={'status'}
												value={values.status ?? ''}
											>
												<option value="">{t('Всі')}</option>
												{Array.isArray(statusesDelivery) &&
													statusesDelivery.map((option) => (
														<option key={option.value} value={option.value}>
															{option.label}
														</option>
													))}
											</select>
										</div>
										<div></div>
									</div>
									<div className="item-filter__buttons">
										<LightGreenButton
											text={t('Застосувати')}
											type="submit"
										/>
										<GreenButton
											text={t('Очистити')}
											type="button"
											onClick={() => {
												// Очищаем поле поиска
												setSearch('');
												// Сбрасываем хук месяца
												monthRangeFilter.reset();
												// Сбрасываем форму к начальным значениям
												resetForm();
												
												// Формируем и отправляем даты при сбросе
												setFilters({
													filter_active: 0,
													offset: 0,
													from_date: monthRangeFilter.fromDate,
													to_date: monthRangeFilter.toDate,
												});
											}}
										/>
									</div>
								</Form>
							</div>
						</div>
					</>
				);
			}}
		</Formik>
	);
};

export default MyOrdersListStoreFilter;

